import countries from 'i18n-iso-countries';
import countriesInEnglish from 'i18n-iso-countries/langs/en.json';
import type { CountryCode } from 'libphonenumber-js';
import type { HomeCountry } from '@peekapak/common/lib/regionAndResidency';

countries.registerLocale(countriesInEnglish);

/**
 * The single country-code boundary (design D13). ISO 3166-1 alpha-3 is canonical
 * across all domain logic, storage, residency derivation, and comparisons;
 * alpha-2 is derived ONLY where an external standard demands it - phone parsing
 * (`libphonenumber-js`) and flag emoji (regional-indicator pairs). Every alpha-3
 * <-> alpha-2 <-> name <-> flag conversion via `i18n-iso-countries` lives here
 * and nowhere else, so the library is imported and its locale registered once.
 */

// The `UNK` sentinel is a resolved-but-unknown home country; it has no real
// alpha-2, name, or flag, so conversions treat it like "no country".
const UNKNOWN_HOME_COUNTRY = 'UNK';

// Base code point of Unicode regional indicator symbol letter A (🇦), and the
// ASCII code of 'A'; a flag emoji is the regional-indicator pair of its alpha-2.
const REGIONAL_INDICATOR_A = 0x1f1e6;
const LETTER_A = 'A'.charCodeAt(0);

const normalize = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim().toUpperCase();
  return trimmed || undefined;
};

/** Convert an ISO alpha-2 country code to canonical alpha-3. */
export function alpha2ToAlpha3(alpha2: string | null | undefined): HomeCountry | undefined {
  const normalized = normalize(alpha2);
  if (!normalized) {
    return undefined;
  }
  const alpha3 = countries.alpha2ToAlpha3(normalized);
  return alpha3 ? (alpha3 as HomeCountry) : undefined;
}

/**
 * Derive the alpha-2 country code from canonical alpha-3, for the external
 * boundaries that require it (phone parsing, flag emoji). Returns undefined for
 * unknown or unmapped countries.
 */
export function alpha3ToAlpha2(alpha3: HomeCountry | null | undefined): CountryCode | undefined {
  const normalized = normalize(alpha3);
  if (!normalized || normalized === UNKNOWN_HOME_COUNTRY) {
    return undefined;
  }
  const alpha2 = countries.alpha3ToAlpha2(normalized);
  return alpha2 ? (alpha2 as CountryCode) : undefined;
}

/** English display name for an alpha-3 country; empty for unknown/`UNK`. */
export function countryNameForAlpha3(alpha3: HomeCountry | null | undefined): string {
  const normalized = normalize(alpha3);
  if (!normalized || normalized === UNKNOWN_HOME_COUNTRY) {
    return '';
  }
  return countries.getName(normalized, 'en') ?? '';
}

/**
 * Flag emoji for an alpha-3 country, built from the regional-indicator pair of
 * its alpha-2 code. Returns '' when the country is unknown or has no alpha-2, so
 * callers can render a neutral placeholder instead.
 */
export function alpha3ToFlagEmoji(alpha3: HomeCountry | null | undefined): string {
  const alpha2 = alpha3ToAlpha2(alpha3);
  if (!alpha2) {
    return '';
  }
  const codePoints = alpha2
    .toUpperCase()
    .split('')
    .map((letter) => REGIONAL_INDICATOR_A + (letter.charCodeAt(0) - LETTER_A));
  return String.fromCodePoint(...codePoints);
}

export interface CountryOption {
  alpha3: HomeCountry;
  name: string;
}

let countryOptionsCache: CountryOption[] | undefined;

/**
 * The full ISO 3166-1 country list as `{ alpha3, name }`, sorted by English
 * name, for the home-country picker (design D6). Built once and cached.
 */
export function listCountryOptions(): CountryOption[] {
  if (!countryOptionsCache) {
    countryOptionsCache = Object.entries(countries.getNames('en'))
      .map(([alpha2, name]) => ({ alpha3: countries.alpha2ToAlpha3(alpha2), name }))
      .filter((option): option is CountryOption => Boolean(option.alpha3))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  return countryOptionsCache;
}
